/**
 * A tile's recipe in the Assistant's words, and back.
 *
 * `specToQuery` turns a proposal into the query string every chart is stored
 * as. Editing a chart that is already on a dashboard needs the other direction:
 * the model is shown the chart as a `PlotSpec`, changes it, and hands the whole
 * spec back. Two things make that safe rather than lossy:
 *
 * * **What a spec cannot say is kept.** Filter rows, outlier handling and
 *   appearance settings have no field in `PlotSpec` (slicers do). They are
 *   listed to the model as kept settings, and `mergeRecipe` carries those params over from
 *   the old query untouched, so "make it a scatter" does not quietly drop the
 *   outlier handling somebody set up by hand.
 * * **Only spec vocabulary is replaced.** Every param `specToQuery` can write
 *   is owned by the spec; everything else belongs to the tile.
 */

import { parseTableColumns, parseTableGroups, parseTableMeasures, parseTableMode } from '../plot/table-config.ts';
import type { PlotSpec, PlotType } from '../../api/types.ts';
import { parseAxisTitle, parseDerivedParam, parseFiltersParam, parseFitsParam } from '../../state/url-sync.ts';
import { isTimeColumn } from '../plot/slicers.ts';
import { defaultPlotState } from '../../state/plot-state.ts';
import { parseNumber } from '../plot/number-config.ts';
import { dialToSpec, parseGaugeDials, parseGaugeLayout } from '../plot/gauge-config.ts';
import { nodeToSpec, parseSankeyDisplay, parseSankeyNodes } from '../plot/sankey-config.ts';

/** Params a `PlotSpec` fully determines - replaced wholesale on an update. */
export const SPEC_PARAMS = [
  'tab', 'ds', 'ship', 'ships', 'vm', 'start', 'end', 'pc', 'pt', 'x', 'yl', 'yr', 'yt', 'yrt',
  'hc', 'bins', 'hy', 'hz', 'hagg', 'hb', 'cc', 'cmeth', 'cord', 'pmo', 'pbc',
  'pbe', 'pcol', 'pagg', 'bmo', 'bcols', 'bcol', 'bgrp', 'lat', 'lon', 'cb', 'fexpr', 'fmode', 'd', 'f', 'colors',
  'nmode', 'ncol', 'nagg', 'nexpr', 'nlabel', 'nunit', 'ndp', 'sk', 'sku', 'skd', 'skm',
] as const;

const list = (v: string | null): string[] => (v ?? '').split(',').filter(Boolean);

const num = (v: string | null): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** A slicer end as the model reads it: an ISO timestamp for a time column. */
function slicerEndForSpec(column: string, value: number | null): number | string | null {
  if (value == null) return null;
  return column === 'timestampNDC' || isTimeColumn(column)
    ? new Date(value * 1000).toISOString().replace('.000Z', 'Z')
    : value;
}

/** Bracket a column name for a derived expression when it is not a plain identifier. */
function operand(name: string): string {
  return /^[A-Za-z_][A-Za-z0-9_]*$/.test(name) ? name : `[${name}]`;
}

/** A `yt`/`yrt` param as a spec field; nothing for auto, so the spec stays short. */
function axisTitleSpec(key: 'y_title' | 'y_right_title', v: string | null): Partial<PlotSpec> {
  const t = parseAxisTitle(v);
  if (t.mode === 'auto') return {};
  return {
    [key]: {
      mode: t.mode,
      ...(t.mode === 'signals' ? { signals: t.signals } : {}),
      ...(t.mode === 'text' ? { text: t.text } : {}),
    },
  };
}

export interface TileSpec {
  spec: PlotSpec;
  /** Settings on the chart a spec cannot express, in words, kept on update. */
  kept: string[];
}

/**
 * Read a stored recipe as a `PlotSpec`.
 *
 * `ships` and `window` are whatever the tile draws for - its own pin, or the
 * dashboard's selection - because a recipe is stored without context and a
 * spec cannot be. Dates are included only when the caller passes a window,
 * which it does only for a tile that pins one.
 */
export function specFromQuery(
  query: string,
  ships: string[],
  window: { start: string; end: string } | null,
): TileSpec {
  const p = new URLSearchParams(query);
  const plotType = (p.get('pt') || 'line') as PlotType;
  const [anchor = '', ...compare] = ships;
  const spec: PlotSpec = {
    ship_id: anchor,
    plot_type: plotType,
    y_left: list(p.get('yl')),
    y_right: list(p.get('yr')),
    ...axisTitleSpec('y_title', p.get('yt')),
    ...axisTitleSpec('y_right_title', p.get('yrt')),
    hist_columns: list(p.get('hc')),
  };
  if (compare.length) {
    spec.compare_ship_ids = compare;
    if (p.get('vm') === 'grid') spec.view_mode = 'grid';
  }
  if (window) {
    spec.start_date = window.start;
    spec.end_date = window.end;
  }
  const x = p.get('x');
  if (x) spec.x_column = x;
  const bins = num(p.get('bins'));
  if (bins != null) spec.hist_bins = bins;

  if (plotType === 'heatmap') {
    const hy = p.get('hy');
    if (hy) spec.y_column = hy;
    const hz = p.get('hz');
    if (hz) {
      spec.z_column = hz;
      spec.z_agg = (p.get('hagg') || 'avg') as NonNullable<PlotSpec['z_agg']>;
    }
    const hb = num(p.get('hb'));
    if (hb != null) spec.bins = hb;
  }
  if (plotType === 'correlation') {
    spec.corr_columns = list(p.get('cc'));
    const method = p.get('cmeth');
    if (method) spec.corr_method = method as NonNullable<PlotSpec['corr_method']>;
    const order = p.get('cord');
    if (order) spec.corr_order = order as NonNullable<PlotSpec['corr_order']>;
  }
  if (plotType === 'number') {
    const number = defaultPlotState();
    parseNumber(p, number);
    Object.assign(spec, {
      number_mode: number.number_mode,
      number_column: number.number_column || null,
      number_agg: number.number_agg,
      number_expression: number.number_expression || null,
      number_label: number.number_label,
      number_unit: number.number_unit,
      number_decimals: number.number_decimals,
    });
  }
  if (plotType === 'gauge') {
    spec.gauge_dials = (parseGaugeDials(p.get('gd')) ?? []).map(dialToSpec);
    spec.gauge_layout = parseGaugeLayout(p.get('glay')) ?? 'ship';
  }
  if (plotType === 'sankey') {
    const display = parseSankeyDisplay(p);
    spec.sankey_nodes = (parseSankeyNodes(p.get('sk')) ?? []).map(nodeToSpec);
    spec.sankey_unit = display.unit;
    spec.sankey_decimals = display.decimals;
    spec.sankey_motion = display.motion;
  }
  if (plotType === 'pie') {
    if (p.get('pmo') === 'bands') {
      spec.pie_mode = 'bands';
      const col = p.get('pbc');
      if (col) spec.pie_band_column = col;
      const edges = list(p.get('pbe')).map(Number).filter(Number.isFinite);
      if (edges.length) spec.pie_band_edges = edges;
    } else {
      spec.pie_columns = list(p.get('pcol'));
      const agg = p.get('pagg');
      if (agg) spec.pie_agg = agg as NonNullable<PlotSpec['pie_agg']>;
    }
  }
  if (plotType === 'box') {
    if (p.get('bmo') === 'groups') {
      spec.box_mode = 'groups';
      const col = p.get('bcol');
      if (col) spec.box_column = col;
      const grp = p.get('bgrp');
      if (grp) spec.box_group_column = grp;
    } else {
      spec.box_columns = list(p.get('bcols'));
    }
  }
  if (plotType === 'table') {
    spec.table_mode = parseTableMode(p);
    spec.table_columns = parseTableColumns(p.get('tcols')) ?? [];
    spec.table_groups = parseTableGroups(p.get('tg')) ?? [];
    spec.table_measures = parseTableMeasures(p.get('tm')) ?? [];
  }
  if (plotType === 'map') {
    const lat = p.get('lat');
    const lon = p.get('lon');
    if (lat) spec.latitude_column = lat;
    if (lon) spec.longitude_column = lon;
  }
  const cb = p.get('cb');
  if (cb) spec.color_by = cb;
  const fexpr = p.get('fexpr');
  if (fexpr) spec.filter_expr = fexpr;

  const d = p.get('d');
  if (d) {
    // Builder-mode signals (a op b) read as the same arithmetic written out, so
    // the model sees one kind of derived signal whatever made it.
    spec.derived = parseDerivedParam(d).map((sig) => {
      if (sig.expr?.trim()) return { name: sig.name, expr: sig.expr };
      const a = sig.operand_a != null ? operand(sig.operand_a) : String(sig.operand_a_const ?? 0);
      const b = sig.operand_b_col != null ? operand(sig.operand_b_col) : String(sig.operand_b_const ?? 0);
      return { name: sig.name, expr: `${a} ${sig.op} ${b}` };
    });
  }
  const f = p.get('f');
  if (f) {
    spec.fits = parseFitsParam(f).map((fit) => ({
      series: fit.series,
      kind: fit.kind as NonNullable<PlotSpec['fits']>[number]['kind'],
      params: fit.params,
      ...(fit.color ? { color: fit.color } : {}),
    }));
  }
  const colors = p.get('colors');
  if (colors) {
    spec.custom_colors = Object.fromEntries(
      colors
        .split(',')
        .map((pair) => {
          const i = pair.lastIndexOf(':');
          return i > 0 ? [pair.slice(0, i), `#${pair.slice(i + 1)}`] : null;
        })
        .filter((e): e is [string, string] => !!e),
    );
  }

  const kept: string[] = [];
  const filters = p.get('filters');
  if (filters) {
    const parsed = parseFiltersParam(filters);
    // Slicers are spec vocabulary; ordinary filter rows are kept settings.
    const slicers = parsed.filter((r) => r.slicer);
    if (slicers.length) {
      spec.slicers = slicers.map((r) => ({
        column: r.column,
        min: slicerEndForSpec(r.column, r.slicer?.min ?? null),
        max: slicerEndForSpec(r.column, r.slicer?.max ?? null),
      }));
    }
    const rows = parsed
      .filter((r) => !r.slicer)
      .map((r) => `${r.column} ${r.op} ${r.value_column ? r.value_column : r.value}`);
    if (rows.length) kept.push(`filter rows (${p.get('logic') === 'OR' ? 'any' : 'all'} of: ${rows.join('; ')})`);
  }
  if (p.get('ol')) kept.push('outlier handling');
  const known = new Set<string>([...SPEC_PARAMS, 'filters', 'logic', 'ol']);
  if ([...p.keys()].some((k) => !known.has(k))) kept.push('appearance settings');
  return { spec, kept };
}

/**
 * The recipe after an update: the spec's params from `next`, everything else
 * from `previous`. Both are context-free recipe strings.
 */
export function mergeRecipe(previous: string, next: string): string {
  const out = new URLSearchParams(next);
  const before = new URLSearchParams(previous);
  const owned = new Set<string>(SPEC_PARAMS);
  // `filters` is shared: its slicers belong to the spec, its ordinary rows to
  // the tile. Keep the old rows, take the new slicers.
  const rows = (before.get('filters') ?? '').split(';;').filter((c) => c && c.split('|')[1] !== '~');
  const slicers = (out.get('filters') ?? '').split(';;').filter((c) => c && c.split('|')[1] === '~');
  const merged = [...rows, ...slicers].join(';;');
  if (merged) out.set('filters', merged);
  else out.delete('filters');
  for (const [key, value] of before) {
    if (key === 'filters') continue;
    if (!owned.has(key) && !out.has(key)) out.append(key, value);
  }
  return out.toString();
}
